import _regeneratorRuntime from "babel-runtime/regenerator";
import _Object$keys from "babel-runtime/core-js/object/keys";
import _extends from "babel-runtime/helpers/extends";
import { take, join, fork, race, cancel, select } from "redux-saga/effects";

export var PAUSE = "PAUSE";
export var UNPAUSE = "UNPAUSE";

/**
 * Takes a saga and wraps it up so it will automatically cancel when a
 * given action comes in.
 *
 * saga = cancelableSaga( regularSaga, [PAUSE, EXIT_APPLICATION] );
 *
 * In that case, if a PAUSE or EXIT_APPLICATION action occurred while the
 * saga was running, the saga would get forcibly canceled. If not, the
 * saga runs and exits normally.
 *
 * The wrapped saga will not throw a cancel exception. (I wonder if we want that option...)
 *
 * @param sagaName A name to show in debug output
 * @param actionsToCancelOn A list of actions that would cause this saga to stop
 * @param saga The saga to run
 */
export var cancelableSaga = function cancelableSaga(saga) {
  var actionsToCancelOn = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [PAUSE];
  var sagaName = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : "unknown";
  return _regeneratorRuntime.mark(function _callee() {
    for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    var actionTakes, task, result;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            // console.log(sagaName, actionsToCancelOn);
            // Create array of take(action) effects based on our actionsToCancelOn:
            actionTakes = actionsToCancelOn.reduce(function (acc, action) {
              acc[action] = take(action);
              return acc;
            }, {});
            _context.next = 3;
            return fork.apply(undefined, [saga].concat(args));

          case 3:
            task = _context.sent;
            _context.next = 6;
            return race(_extends({}, actionTakes, {
              saga: join(task)
            }));

          case 6:
            result = _context.sent;

            if (result.saga) {
              _context.next = 11;
              break;
            }

            console.info("'Canceling saga " + sagaName + "(" + args + ") because " + _Object$keys(result).join(" "), task, actionsToCancelOn);
            _context.next = 11;
            return cancel(task);

          case 11:
            return _context.abrupt("return", result.saga);

          case 12:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, this);
  });
};

/**
 * Takes a saga and wraps it up so it's "pausable"
 *
 * "Pausable" means it'll pause grabbing values from the saga
 * after it's next yield when a PAUSE action comes in, and then resume
 * when an unpause comes in.
 *
 * saga = pausableSaga( regularSaga );
 *
 * @param isPausedSelector A selector for your app that returns a boolean determining if app is paused or not.
 * @param name A name to show in debug output
 * @param saga
 */

export var pausableSaga = function pausableSaga(saga) {
  var name = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "unknown";
  var isPausedSelector = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
  return _regeneratorRuntime.mark(function _callee2() {
    var generator,
        previous,
        paused,
        nextGenerated,
        nextEffect,
        _args2 = arguments;
    return _regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            generator = saga.apply(undefined, _args2);
            previous = null;

          case 2:
            if (!true) {
              _context2.next = 22;
              break;
            }

            paused = false;

            if (!isPausedSelector) {
              _context2.next = 8;
              break;
            }

            _context2.next = 7;
            return select(isPausedSelector);

          case 7:
            paused = _context2.sent;

          case 8:
            if (!paused) {
              _context2.next = 13;
              break;
            }

            // If we're paused, wait for an unpause event
            console.log("We are paused, waiting on " + name);
            _context2.next = 12;
            return take(UNPAUSE);

          case 12:
            console.log("Resuming saga " + name);

          case 13:
            nextGenerated = generator.next(previous);

            if (!nextGenerated.done) {
              _context2.next = 16;
              break;
            }

            return _context2.abrupt("return", nextGenerated.value);

          case 16:
            nextEffect = nextGenerated.value;
            _context2.next = 19;
            return nextEffect;

          case 19:
            previous = _context2.sent;
            _context2.next = 2;
            break;

          case 22:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2, this);
  });
};

export var cancelableAndPausableSaga = function cancelableAndPausableSaga(saga, cancelActions, name, isPausedSelector) {
  return cancelableSaga(pausableSaga(saga, name, isPausedSelector), cancelActions, name);
};

/**
 * Only invoke one saga at a time and discard new sagas while the first one is running.
 * @param actionType
 * @param saga
 */
export var takeFirst = function takeFirst(actionType, saga) {
  return fork(_regeneratorRuntime.mark(function _callee3() {
    var task, action;
    return _regeneratorRuntime.wrap(function _callee3$(_context3) {
      while (1) {
        switch (_context3.prev = _context3.next) {
          case 0:
            task = void 0;

          case 1:
            if (!true) {
              _context3.next = 11;
              break;
            }

            _context3.next = 4;
            return take(actionType);

          case 4:
            action = _context3.sent;

            if (!(!task || !task.isRunning())) {
              _context3.next = 9;
              break;
            }

            _context3.next = 8;
            return fork(saga, action);

          case 8:
            task = _context3.sent;

          case 9:
            _context3.next = 1;
            break;

          case 11:
          case "end":
            return _context3.stop();
        }
      }
    }, _callee3, this);
  }));
};

// Previous implementation for reference, it had the possibility of a race condition preventing it from unpausing.

// /**
//  * Helper for pauseableSaga that creates a generator that yields exactly one value and returns the result.
//  * Required since you can only fork on a generator.
//  *
//  * @param nextEffect The effect to yield
//  */
// const generateOne = (nextEffect) => function*() {
//   // console.log('in generateOne next effect', nextEffect);
//   return yield nextEffect;
// }

// export const pausableSaga = (saga) => function*(action) {
//   let generator = saga(action);
//   let previous = null;
//
//   while(true) {
//     // console.log('previous input', previous);
//
//     const nextGenerated = generator.next(previous);
//     const nextEffect = nextGenerated.value;
//     // console.log('next generated value', nextGenerated)
//     if(nextGenerated.done) {
//       return previous; // No more effects from this saga, so return the last result.
//     }
//     const nextTask = yield fork(generateOne(nextEffect))
//
//     const result = yield race({
//       paused: take(PAUSE),
//       generatedValue: join(nextTask)
//     });
//
//     // console.log('pausableSaga race result', result)
//
//     if(result.paused) {
//       yield take(UNPAUSE);
//       previous = yield join(nextTask);
//       // console.log('unpaused', previous)
//     }
//     else {
//       previous = result.generatedValue;
//     }
//   }
// };


//////////////////
// WEBPACK FOOTER
// ./node_modules/@rosetta/redux-utils/es/sagautils.js
// module id = 605
// module chunks = 0